import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, Wallet, CreditCard, Lock } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, fmt } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [
  ["full_name", "Full name"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["address1", "Address"],
  ["city", "City"],
  ["state", "State / Region"],
  ["zip", "ZIP / Postal"],
  ["country", "Country"],
];

const METHOD_ICON = {
  bank_transfer: Landmark,
  paypal: CreditCard,
  crypto: Wallet,
};

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [selected, setSelected] = useState("");
  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "", address1: "", city: "", state: "", zip: "", country: "Australia",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payment-methods").then((r) => {
      setMethods(r.data.methods);
      if (r.data.methods.length) setSelected(r.data.methods[0].id);
    });
  }, []);

  const paypalMethod = useMemo(() => methods.find((m) => m.id === "paypal"), [methods]);
  const isPaypal = selected === "paypal";

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="checkout-empty">
        <h1 className="text-3xl font-medium">Your cart is empty</h1>
        <p className="mt-3 text-slate-400">Add a few compounds before checking out.</p>
        <button onClick={() => navigate("/shop")} className="mt-8 bg-cyan-500 text-slate-950 font-semibold px-6 py-3">Browse catalog</button>
      </div>
    );
  }

  const shipping = 15;
  const total = subtotal + shipping;

  const shippingComplete = FIELDS.every(([k]) => (form[k] || "").trim().length > 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Choose a payment method"); return; }
    if (isPaypal) {
      toast.error("Use the PayPal button below to complete payment");
      return;
    }
    setBusy(true);
    try {
      if (selected === "bank_transfer") {
        const payload = {
          items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_label: i.variant_label || null })),
          shipping: form,
        };
        const { data } = await api.post("/checkout/bank-transfer", payload);
        navigate(`/checkout/bank-transfer/${data.reference}`);
      } else {
        toast.error("This payment method isn't enabled yet.");
        setBusy(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create order");
      setBusy(false);
    }
  };

  // ----- PayPal Smart Buttons callbacks -----
  const paypalCreateOrder = async () => {
    if (!shippingComplete) {
      toast.error("Please fill in all shipping fields first");
      throw new Error("shipping incomplete");
    }
    const payload = {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_label: i.variant_label || null })),
      shipping: form,
    };
    const { data } = await api.post("/checkout/paypal/create", payload);
    return data.paypal_order_id;
  };

  const paypalOnApprove = async (data) => {
    try {
      const { data: result } = await api.post(`/checkout/paypal/capture/${data.orderID}`);
      toast.success("Payment received — thank you!");
      navigate(`/checkout/bank-transfer/${result.reference}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed — please contact support");
    }
  };

  const paypalOnError = (err) => {
    console.error("PayPal error", err);
    toast.error("PayPal error — please try again or use bank transfer");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="checkout-page">
      <div className="label-mono text-cyan-400 mb-3">/ CHECKOUT</div>
      <h1 className="text-4xl font-medium tracking-tight mb-10">Complete your order</h1>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <section>
            <div className="label-mono text-slate-400 mb-4">Shipping Details</div>
            <div className="grid sm:grid-cols-2 gap-4">
              {FIELDS.map(([k, l]) => (
                <label key={k} className={`block ${["address1"].includes(k) ? "sm:col-span-2" : ""}`}>
                  <span className="label-mono text-slate-500 text-[10px]">{l}</span>
                  <input
                    required
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`ship-${k}`}
                    className="mt-1.5 w-full bg-transparent border border-white/10 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 outline-none px-3 h-11 text-sm"
                  />
                </label>
              ))}
            </div>
          </section>

          <section>
            <div className="label-mono text-slate-400 mb-4">Payment Method</div>
            <div className="space-y-3" data-testid="payment-methods">
              {methods.length === 0 && (
                <div className="text-sm text-slate-500 border border-white/10 p-4">No payment methods configured.</div>
              )}
              {methods.map((m) => {
                const Icon = METHOD_ICON[m.id] || Lock;
                const active = selected === m.id;
                return (
                  <label
                    key={m.id}
                    data-testid={`pm-${m.id}`}
                    className={`flex items-start gap-4 p-5 border cursor-pointer transition ${active ? "border-cyan-500 bg-cyan-500/5" : "border-white/10 hover:border-cyan-500/30"}`}
                  >
                    <input
                      type="radio"
                      name="method"
                      checked={active}
                      onChange={() => setSelected(m.id)}
                      className="mt-1 accent-cyan-500"
                    />
                    <Icon size={22} className={active ? "text-cyan-400 mt-0.5" : "text-slate-400 mt-0.5"} strokeWidth={1.5} />
                    <div className="flex-1">
                      <div className="font-medium">{m.label}</div>
                      <div className="text-xs text-slate-400 mt-1 leading-relaxed">{m.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="border border-white/5 p-6 sticky top-24">
            <div className="label-mono text-cyan-400 mb-4">Order Summary</div>
            <div className="space-y-3 max-h-64 overflow-auto pr-1">
              {items.map((i, idx) => (
                <div key={`${i.product_id}-${i.variant_label || ""}-${idx}`} className="flex justify-between text-sm" data-testid={`summary-${i.product_id}`}>
                  <span className="text-slate-300 truncate pr-2">
                    {i.name}
                    {i.variant_label && <span className="text-cyan-400/80"> · {i.variant_label}</span>}
                    {" "}<span className="text-slate-500">×{i.quantity}</span>
                  </span>
                  <span className="font-mono text-slate-300">{fmt(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-2 text-sm border-t border-white/5 pt-4">
              <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span className="font-mono">{fmt(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Shipping</span><span className="font-mono">{fmt(shipping)}</span></div>
              <div className="flex justify-between text-lg pt-2 border-t border-white/5">
                <span>Total</span><span className="font-mono text-cyan-400" data-testid="checkout-total">{fmt(total)}</span>
              </div>
            </div>

            {isPaypal && paypalMethod ? (
              <div className="mt-6" data-testid="paypal-buttons-wrap">
                <PayPalScriptProvider
                  options={{
                    "client-id": paypalMethod.client_id,
                    currency: "AUD",
                    intent: "capture",
                  }}
                >
                  <PayPalButtons
                    style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                    disabled={!shippingComplete}
                    createOrder={paypalCreateOrder}
                    onApprove={paypalOnApprove}
                    onError={paypalOnError}
                  />
                </PayPalScriptProvider>
                {!shippingComplete && (
                  <p className="mt-2 text-[10px] text-amber-300/80">Fill in all shipping fields above to enable PayPal.</p>
                )}
              </div>
            ) : (
              <button
                type="submit"
                disabled={busy || !selected}
                data-testid="pay-btn"
                className="mt-6 w-full bg-cyan-500 hover:bg-cyan-400 disabled:bg-slate-700 text-slate-950 font-semibold py-3"
              >
                {busy ? "Creating order…" : "Place Order"}
              </button>
            )}
            <p className="mt-4 text-[10px] text-slate-500 leading-relaxed">
              By placing this order you confirm the products are for in-vitro research use only.
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
}
